import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldCheck, ArrowLeft, User, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/lib/LanguageContext";
import { CURRENCY_SYMBOLS } from "@/lib/constants";

export default function WithdrawalPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [fullName, setFullName] = useState("");
  const [iban, setIban] = useState("");
  const [email, setEmail] = useState("");
  const [balance, setBalance] = useState("");
  const [currency, setCurrency] = useState("XPF");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const storedBalance = sessionStorage.getItem("userBalance");
    if (!storedBalance) {
      navigate("/");
      return;
    }
    setBalance(storedBalance);
    setCurrency(sessionStorage.getItem("userCurrency") || "XPF");
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      toast.error(t("enterFullName"));
      return;
    }
    if (!iban.trim()) {
      toast.error(t("enterIban"));
      return;
    }
    if (!email.trim()) {
      toast.error(t("enterEmail"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t("invalidEmail"));
      return;
    }

    setIsLoading(true);

    sessionStorage.setItem("withdrawalName", fullName);
    sessionStorage.setItem("withdrawalIban", iban);
    sessionStorage.setItem("withdrawalEmail", email);

    setTimeout(() => {
      setIsLoading(false);
      navigate("/bank-auth");
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-[#64748B] hover:text-[#0F172A]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <img src="/logo-fmi.png" alt="FMI" className="w-8 h-8 rounded-lg" />
            <span className="font-semibold text-[#0F172A]">{t("withdrawalRequest")}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto p-4 sm:p-6 pb-32">
        {/* Amount Display */}
        <div className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-xl p-4 mb-6 text-white animate-fade-in">
          <p className="text-xs uppercase tracking-wider text-white/70 mb-1">{t("amountToTransfer")}</p>
          <p className="text-lg font-bold" data-testid="withdrawal-amount">{balance} {CURRENCY_SYMBOLS[currency] || currency}</p>
        </div>

        {/* Form */}
        <Card className="form-container border-0 shadow-lg animate-fade-in" style={{ animationDelay: '0.1s' }} data-testid="withdrawal-form-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#0F172A]">
              {t("beneficiaryInfo")}
            </CardTitle>
            <p className="text-sm text-[#64748B]">
              {t("enterBankDetails")}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("fullName")}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Jean Dupont"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="fullname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="iban" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  IBAN
                </Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="iban"
                    type="text"
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    value={iban}
                    onChange={(e) => setIban(e.target.value.toUpperCase())}
                    className="pl-10 h-10 text-sm font-mono"
                    data-testid="iban-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("emailAddress")}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="jean.dupont@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                  disabled={isLoading}
                  data-testid="validate-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("validating")}
                    </span>
                  ) : (
                    t("validate")
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("secureTransaction")}</span>
          </div>
        </div>
        <p className="text-center text-xs text-[#64748B] mt-2">
          {t("dataProtected")}
        </p>
      </div>
    </div>
  );
}
